import {
    FormField, FormOption, FormValidation, InputIconType, InputPrefixSuffixType, InputType,
    OptionInputType, Size, ValidationPattern
} from "@odin/odin-core";
import { CountryConvertService } from '@odin/odin-core';
import { CurrencyPipe } from "@angular/common";

function resolveCurrencyIcon(countryData: ReturnType<CountryConvertService['GetCountryByCurrencyNumericCode']>, currencyPipe: CurrencyPipe): { iconType: InputIconType, symbol: string } {
    if (!countryData) return { iconType: InputIconType.TEXT, symbol: '£' };
    const currencyParse = Intl.NumberFormat('en', { style: 'currency', currency: countryData.currencyCode }).formatToParts();
    const symbol = currencyParse.find(part => part.type === 'currency')?.value || currencyPipe.transform(1, countryData.currencyCode) || '£';
    if (!symbol || symbol === countryData.currencyCode) {
        return { iconType: InputIconType.ICON, symbol: 'payments' };
    }
    return { iconType: InputIconType.TEXT, symbol };
}

export default ({
    amountFieldName,
    currencyFieldName,
    midCurrencies,
    countryConvertService,
    currencyPipe,
    onCurrencyChange
}: {
    amountFieldName: string,
    currencyFieldName: string,
    midCurrencies: string[],
    countryConvertService: CountryConvertService,
    currencyPipe: CurrencyPipe,
    onCurrencyChange?: (iconType: InputIconType, symbol: string) => void
}) => {
    const availableCurrencies = [];

    if (!midCurrencies || midCurrencies.length === 0) {
        availableCurrencies.push('826'); // Default to GBP if no currencies are available
    } else {
        for (const currency of midCurrencies) {
            availableCurrencies.push(currency);
        }
    }

    const availableCurrencyOptions = availableCurrencies.map((currency) => {
        const countryData = countryConvertService.GetCountryByCurrencyNumericCode(currency);
        if (countryData === undefined) return null;

        const currencyParse = Intl.NumberFormat('en', { style: 'currency', currency: countryData.currencyCode }).formatToParts();
        const currencySymbol = currencyParse.find(part => part.type === 'currency')?.value || currencyPipe.transform(1, countryData.currencyCode);

        const viewName = countryData.currencyCode !== currencySymbol
            ? `${countryData.currencyCode} ${currencySymbol}`
            : countryData.currencyCode;

        return new FormOption(currency, viewName);
    }).filter(ac => ac !== null)

    let firstCurrencySymbol = '£';
    let firstIconType = InputIconType.TEXT;
    if (availableCurrencies.length > 0) {
        const countryData = countryConvertService.GetCountryByCurrencyNumericCode(availableCurrencies[0]);
        const { iconType, symbol } = resolveCurrencyIcon(countryData, currencyPipe);
        firstIconType = iconType;
        firstCurrencySymbol = symbol;
    }

    const amountFieldProperties = [
        amountFieldName,
        'Transaction Amount',
        InputType.Number,
        null,
        availableCurrencies.length > 1 ? Size.TwoThird : Size.Full,
        new FormValidation()
            .Required()
            .MinValue(0.01, 'Minimum transaction value not met')
            .MaxValue(999999.99, 'Maximum transaction value exceeded')
            .PatternMatch(ValidationPattern.Decimal, 'Amount must be a number'),
        false,
        {
            prefixIcon: new InputPrefixSuffixType(
                firstIconType,
                firstCurrencySymbol,
            ),
        },
        undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (formfield: FormField, value: any) => {
            if (value !== '') {
                const numericValue = parseFloat(value);
                formfield.value = numericValue.toFixed(2);
                if (isNaN(value)) formfield.value = '0.00';
            }
        }
    ] as const;

    const currencyFieldProperties = [
        currencyFieldName,
        'Currency',
        availableCurrencyOptions.length > 1 ? OptionInputType.Dropdown : OptionInputType.Hidden,
        availableCurrencyOptions[0]?.value,
        [...availableCurrencyOptions] as FormOption[],
        Size.Third,
        new FormValidation(),
        false,
        undefined,
        (value: string | number | boolean | Date | null) => {
            if (value && typeof value === 'string' && onCurrencyChange) {
                const countryData = countryConvertService.GetCountryByCurrencyNumericCode(value);
                const { iconType, symbol: symbolValue } = resolveCurrencyIcon(countryData, currencyPipe);
                
                onCurrencyChange(iconType, symbolValue);
            }
        }
    ] as const;

    return { amountFieldProperties, currencyFieldProperties };
}

